<template>
    <div>
        <!-- Selected cause list -->
        <q-table
            v-if="!isSelectMode"
            @request="onRequest"
            :filter="selectedCausesTable.search"
            :rows="causes"
            :columns="selectedCausesTable.cols"
            v-model:pagination="selectedCausesTable.pagination"
            :rows-per-page-options="[
                selectedCausesTable.pagination.rowsPerPage
            ]"
            :class="$q.screen.gt.lg ? 'sticky-table causes-table' : ''"
            class="q-ma-none"
            row-key="id"
            binary-state-sort
            :no-data-label="$t('No data available')"
            flat
            :grid="$q.screen.lt.lg"
        >
            <!-- search bar -->
            <template v-slot:top>
                <div class="col-12">
                    <div class="text-h5 text-weight-medium">
                        {{ $t('Downtime causes for') + ' ' + asset?.name }}
                    </div>
                    <div class="page-description">
                        {{ $t('Manage all causes linked to this asset.') }}
                    </div>
                </div>
                <div
                    class="row fit q-mb-sm"
                    :class="
                        $q.screen.lt.sm
                            ? ''
                            : 'wrap justify-between items-start content-start'
                    "
                >
                    <q-input
                        data-test="search-cause-input"
                        v-model="selectedCausesTable.search"
                        @clear="removeFilteredCauseFromStore"
                        :placeholder="$t('Search downtime cause')"
                        :class="
                            $q.screen.lt.sm ? 'col-12 q-mb-md' : 'col q-mr-md'
                        "
                        debounce="750"
                        clearable
                        outlined
                        dense
                        @input="setFilteredCauseInStore"
                        color="accent"
                    >
                        <template v-slot:prepend>
                            <q-icon name="search" />
                        </template>
                    </q-input>
                    <q-btn
                        id="add-cause-button"
                        @click="isSelectMode = true"
                        :label="$t('Add causes')"
                        :disable="$userCannot('edit', 'assets')"
                        class="table-header__button"
                        :class="$q.screen.lt.sm ? 'col-12' : ''"
                        color="accent"
                        size="md"
                    ></q-btn>
                </div>
            </template>

            <!-- Type of loss column -->
            <template v-slot:body-cell-type_of_loss="props">
                <q-td :props="props">
                    <q-badge
                        outline
                        align="middle"
                        :color="getLossOptions(props.row.type_of_loss).color"
                        >{{
                            $t(getLossOptions(props.row.type_of_loss).label)
                        }}</q-badge
                    >
                </q-td>
            </template>

            <!-- Planned column -->
            <template v-slot:body-cell-planned="props">
                <q-td :props="props">
                    <q-icon
                        :name="
                            props.row.planned
                                ? 'fas fa-check-circle'
                                : 'fas fa-times-circle'
                        "
                        :color="props.row.planned ? 'positive' : 'negative'"
                    ></q-icon>
                </q-td>
            </template>

            <!-- Equipment failure column -->
            <template v-slot:body-cell-equipment_failure="props">
                <q-td :props="props">
                    <q-icon
                        :name="
                            props.row.equipment_failure
                                ? 'fas fa-check-circle'
                                : 'fas fa-times-circle'
                        "
                        :color="
                            props.row.equipment_failure
                                ? 'positive'
                                : 'negative'
                        "
                    ></q-icon>
                </q-td>
            </template>

            <!-- actions column -->
            <template v-slot:body-cell-actions="props">
                <q-td :props="props">
                    <div class="row q-gutter-md">
                        <q-btn
                            @click="
                                $router.push({
                                    name: 'cause_update',
                                    params: {
                                        facilityId: facilityId,
                                        causeId: props.row.id
                                    },
                                    state: {
                                        name: props.row.name,
                                        code: props.row.code,
                                        description: props.row.description,
                                        planned: props.row.planned,
                                        target_down_time:
                                            props.row.target_down_time,
                                        category: props.row.category,
                                        category_name: props.row.category_name,
                                        type_of_loss: props.row.type_of_loss,
                                        equipment_failure:
                                            props.row.equipment_failure,
                                        lock_transition_to_running:
                                            props.row.lock_transition_to_running
                                    }
                                })
                            "
                            class="table__action-button"
                            color="accent"
                            icon="fas fa-edit"
                            outline
                        >
                            <q-tooltip>{{ $t('Edit cause') }}</q-tooltip>
                        </q-btn>
                        <q-btn
                            id="remove-cause-button"
                            @click="remove(props.row)"
                            :disable="$userCannot('edit', 'assets')"
                            :loading="props.row.isLoading"
                            class="table__action-button"
                            color="negative"
                            icon="fas fa-trash-alt"
                            outline
                        >
                            <template v-slot:loading>
                                <q-spinner />
                            </template>
                            <q-tooltip>{{ $t('Remove cause') }}</q-tooltip>
                        </q-btn>
                    </div>
                </q-td>
            </template>

            <!-- No causes -->
            <template v-slot:no-data>
                <span class="text-weight-regular">
                    {{
                        `${$t('No downtime causes are linked to this asset.')}.`
                    }}
                    <q-btn
                        @click="isSelectMode = true"
                        :label="$t('Click here to add some.')"
                        class="inline-button"
                        color="accent"
                        dense
                        flat
                        no-caps
                    ></q-btn>
                </span>
            </template>

            <!-- card template for small devices -->
            <template v-slot:item="props">
                <cause-card
                    @edit="
                        $router.push({
                            name: 'cause_update',
                            params: {
                                facilityId: facilityId,
                                causeId: props.row.id
                            },
                            state: {
                                name: props.row.name,
                                code: props.row.code,
                                description: props.row.description,
                                planned: props.row.planned,
                                target_down_time: props.row.target_down_time,
                                category: props.row.category,
                                category_name: props.row.category_name,
                                type_of_loss: props.row.type_of_loss,
                                equipment_failure: props.row.equipment_failure,
                                lock_transition_to_running:
                                    props.row.lock_transition_to_running
                            }
                        })
                    "
                    @delete="remove(props.row)"
                    :name="props.row.name"
                    :lossType="props.row.type_of_loss"
                    :categoryName="props.row.category_name"
                    :planned="props.row.planned"
                    :description="props.row.description"
                    :targetDowntime="props.row.target_down_time"
                    :equipmentFailure="props.row.equipment_failure"
                ></cause-card>
            </template>
        </q-table>

        <!-- Available cause list -->
        <q-table
            v-else
            @request="onRequest"
            :filter="availableCausesTable.search"
            :rows="causes"
            :columns="availableCausesTable.cols"
            v-model:pagination="availableCausesTable.pagination"
            :rows-per-page-options="[
                availableCausesTable.pagination.rowsPerPage
            ]"
            v-model:selected="availableCausesTable.selected"
            class="sticky-table causes-table q-ma-none"
            table-style="max-height: none;"
            selection="multiple"
            row-key="id"
            binary-state-sort
            :no-data-label="$t('No data available')"
            flat
        >
            <!-- search bar -->
            <template v-slot:top>
                <div class="col-12 text-h6 q-mb-md">
                    <q-btn
                        @click="confirmReturn"
                        :label="$t('Return to asset downtime causes')"
                        icon="fas fa-chevron-left"
                        color="primary"
                        flat
                    ></q-btn>
                </div>

                <div class="col-12 text-h6 q-mb-md">
                    {{ $t('Select downtime causes for this asset') }}
                </div>
                <div
                    class="fit row wrap justify-between items-start content-start"
                >
                    <q-input
                        data-test="search-cause-input"
                        v-model="availableCausesTable.search"
                        @clear="availableCausesTable.search = ''"
                        :placeholder="$t('Search downtime cause')"
                        class="col q-mr-md"
                        type="string"
                        debounce="750"
                        clearable
                        outlined
                        dense
                        color="accent"
                    >
                        <template v-slot:prepend>
                            <q-icon name="search" />
                        </template>
                    </q-input>
                    <div>
                        <q-btn
                            data-test="confirm-select-button"
                            @click="confirmSelect"
                            :label="$t('Confirm selection')"
                            class="table-header__button"
                            color="positive"
                            size="md"
                        />
                    </div>
                </div>
            </template>

            <!-- Planned column -->
            <template v-slot:body-cell-planned="props">
                <q-td :props="props">
                    <q-icon
                        :name="
                            props.row.planned
                                ? 'fas fa-check-circle'
                                : 'fas fa-times-circle'
                        "
                        :color="props.row.planned ? 'positive' : 'negative'"
                    ></q-icon>
                </q-td>
            </template>

            <!-- Type of loss column -->
            <template v-slot:body-cell-type_of_loss="props">
                <q-td :props="props">
                    <q-badge
                        outline
                        align="middle"
                        :color="getLossOptions(props.row.type_of_loss).color"
                        >{{
                            $t(getLossOptions(props.row.type_of_loss).label)
                        }}</q-badge
                    >
                </q-td>
            </template>
        </q-table>
    </div>
</template>

<script>
import CauseCard from '@components/CauseCard.vue';
import UpdateURL from '@mixins/UpdateURL.vue';
import { mapActions, mapState } from 'pinia';
import { useSharedStore } from '@stores/shared.js';
import { useFiltersStore } from '@stores/filters.js';

import { getLossOptions } from '@models/causes.js';

export default {
    name: 'AssetCauseList',
    components: { CauseCard },
    mixins: [UpdateURL],
    props: {
        asset: {
            type: [Object],
            required: true
        },
        facilityId: {
            type: [Number, String],
            required: true
        }
    },
    watch: {
        isSelectMode: {
            handler() {
                // display list of all the causes
                // this.selectedCausesTable.search = '';
                this.initCauseList();
            }
        }
    },
    data() {
        return {
            causes: [],
            isSelectMode: false,

            // causes linked to the asset
            selectedCausesTable: {
                cols: [
                    {
                        align: 'left',
                        field: 'type_of_loss',
                        name: 'type_of_loss',
                        label: this.$t('Type of Loss'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'category_name',
                        name: 'category',
                        label: this.$t('Category'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'name',
                        name: 'name',
                        label: this.$t('Name'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'code',
                        name: 'code',
                        label: this.$t('Code'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'planned',
                        name: 'planned',
                        label: this.$t('Planned'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'equipment_failure',
                        name: 'equipment_failure',
                        label: this.$t('Equipment failure'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'target_down_time',
                        name: 'target_down_time',
                        label: this.$t('Target downtime (min)'),
                        format: (val, row) => {
                            if (!row.planned) {
                                return '-';
                            }
                            return val;
                        }
                    },
                    {
                        align: 'left',
                        field: 'description',
                        name: 'description',
                        label: this.$t('Description'),
                        format(val) {
                            // truncate description to prevent overflow
                            if (val.length > 30) {
                                return `${val.substring(0, 30)}...`;
                            } else {
                                return val;
                            }
                        }
                    },
                    {
                        align: 'left',
                        field: 'actions',
                        name: 'actions',
                        label: this.$t('Actions')
                    }
                ],
                pagination: {
                    sortBy: '',
                    descending: false,
                    page: 1,
                    rowsPerPage: 30,
                    rowsNumber: null
                },
                search: ''
            },
            // causes that can be linked to this asset
            availableCausesTable: {
                cols: [
                    {
                        align: 'left',
                        field: 'name',
                        name: 'name',
                        label: this.$t('Name'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'code',
                        name: 'code',
                        label: this.$t('Code'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'planned',
                        name: 'planned',
                        label: this.$t('Planned'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'type_of_loss',
                        name: 'type_of_loss',
                        label: this.$t('Type of Loss'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'description',
                        name: 'description',
                        label: this.$t('Description')
                    }
                ],
                pagination: {
                    sortBy: '',
                    descending: false,
                    page: 1,
                    rowsPerPage: 30,
                    rowsNumber: null
                },
                search: '',
                selected: []
            }
        };
    },
    computed: {
        ...mapState(useFiltersStore, ['getStoredAssetFilter']),
        /**
         * Base URL to use to fetch causes.
         * @return {string}
         */
        baseURL() {
            if (this.isSelectMode) {
                return `assets/${this.asset.id}/available_causes/`;
            } else {
                return `assets/${this.asset.id}/causes/`;
            }
        },
        /**
         * Get URL with query parameters.
         * @return {String}
         */
        url() {
            // set active table
            let table = null;
            if (this.isSelectMode) {
                table = this.availableCausesTable;
            } else {
                table = this.selectedCausesTable;
            }
            // get current pagination state attributes
            let { page, rowsPerPage, sortBy, descending } = table.pagination;

            // order by category name
            if (sortBy === 'category') {
                sortBy = 'category__name';
            }

            // update URL
            return this.updateURL({
                baseURL: this.baseURL,
                params: {
                    search: table.search,
                    ordering: descending ? `-${sortBy}` : `${sortBy}`,
                    page: page,
                    // add 'facility' in query if we are in select mode
                    ...(this.isSelectMode && {
                        facility: this.facilityId
                    })
                }
            });
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        ...mapActions(useFiltersStore, ['setAssetFilter']),
        /**
         * Get causes from the server.
         */
        async getCauses() {
            // Send the API request only if asset.id is an integer
            if (this.asset) {
                await this.$axios
                    .get(this.url)
                    .then(response => {
                        // push data
                        this.causes = [];
                        this.causes.push(...response.data.results);

                        // update total items count for table
                        if (this.isSelectMode) {
                            this.availableCausesTable.pagination.rowsNumber =
                                response.data.count;
                        } else {
                            this.selectedCausesTable.pagination.rowsNumber =
                                response.data.count;
                        }
                    })
                    .catch(error => {
                        this.$notifyError(
                            this.$t(
                                'Failed to load causes. Please try again later.'
                            ),
                            error
                        );
                    })
                    .finally(() => {
                        this.stopLoading();
                    });
            } else {
                this.$notifyError(
                    this.$t('Failed to load causes. Please try again later.')
                );
            }
        },
        /**
         * Fetch data from the server. Triggered by any table sort/search/page changes.
         * @param {Object} props
         * @param {Object} props.pagination
         * @param {string} props.filter
         */
        async onRequest(props) {
            // update pagination object for table
            if (this.isSelectMode) {
                this.availableCausesTable.pagination = props.pagination;
            } else {
                this.selectedCausesTable.pagination = props.pagination;
            }

            // begin loading
            this.beginLoading(`${this.$t('Loading causes')}...`);

            // // clear existing data
            // this.causes = [];
            // fetch data from the server
            await this.getCauses();
        },
        /**
         * Unselect a cause for this asset.
         */
        async remove(row) {
            this.$confirmDelete(
                this.$t(
                    'Are you sure you want to remove {item} from {asset}?',
                    {
                        item: row.name,
                        asset: this.asset.name
                    }
                )
            ).onOk(async () => {
                // begin loading
                row.isLoading = true;
                // unlink cause from asset
                this.$axios
                    .post(`assets/${this.asset.id}/remove_cause/`, {
                        cause_ids: [row.id]
                    })
                    .then(response => {
                        // remove item off the table
                        let causeIndex = this.causes.findIndex(
                            cause => cause.id === row.id
                        );
                        this.causes.splice(causeIndex, 1);

                        // decrement total row count
                        this.selectedCausesTable.pagination.rowsNumber--;

                        // notify user
                        this.$notifySuccess(
                            this.$t('Removed cause from asset')
                        );
                    })
                    .catch(error => {
                        this.$notifyError(
                            this.$t(
                                'Failed to remove cause from this asset. Please try again later.'
                            ),
                            error
                        );
                    })
                    .finally(() => {
                        row.isLoading = false;
                    });
            });
        },
        /**
         * Confirm causes selected for this asset.
         */
        confirmSelect() {
            // extract ids from selected causes
            const causeIds = this.availableCausesTable.selected.map(
                cause => cause.id
            );
            this.$axios
                .post(`assets/${this.asset.id}/add_cause/`, {
                    cause_ids: causeIds
                })
                .then(response => {
                    this.$notifySuccess(this.$t('Updated causes in asset'));
                })
                .catch(error => {
                    this.$notifyError(
                        this.$t(
                            'Failed to update causes in this asset. Please try again later.'
                        ),
                        error
                    );
                })
                .finally(() => {
                    // return to list view
                    this.isSelectMode = false;
                    // empty selected causes
                    this.availableCausesTable.selected = [];
                });
        },
        /**
         * Cancel causes selection.
         */
        confirmReturn() {
            this.isSelectMode = false;
            this.availableCausesTable.selected = [];
        },

        getLossOptions: value => getLossOptions(value),

        /**
         * Update the filtered cause for particular facility,
         * and particular asset in the store.
         * Depends on the value of selectedCausesTable.search.
         */
        setFilteredCauseInStore() {
            this.setAssetFilter({
                facilityId: this.facilityId,
                assetId: this.asset.id,
                filter: 'cause',
                value: this.selectedCausesTable.search
            });
        },

        /**
         * Remove the filtered cause of per facility and per asset
         * from the store. And reset the search key of this.selectedCausesTable
         * to its inital value.
         */
        removeFilteredCauseFromStore() {
            this.setAssetFilter({
                facilityId: this.facilityId,
                assetId: this.asset.id,
                filter: 'cause',
                value: ''
            });
            this.selectedCausesTable.search = '';
        },

        /**
         * Select the pagination on based of view,
         * either selected cause list view or available
         * cause list view, and make the request to the
         * backend to get the list of all causes
         * for this asset.
         */
        initCauseList() {
            let pagination = {};
            if (this.isSelectMode) {
                pagination = this.availableCausesTable.pagination;
            } else {
                pagination = this.selectedCausesTable.pagination;
            }
            // load list of causes
            this.onRequest({
                pagination: pagination,
                filter: ''
            });
        }
    },
    created() {
        // get the store filtered cause name
        const storedFilteredCause = this.getStoredAssetFilter({
            facilityId: this.facilityId,
            assetId: this.asset.id,
            filter: 'cause'
        });
        // filtered cause string.
        this.selectedCausesTable.search = storedFilteredCause;
        // if there is nothing in the store for filtered causes
        // get all the causes from the database for this particular
        // asset, otherwise get the causes related to the stored
        // filtered.
        if (!this.selectedCausesTable.search) {
            this.initCauseList();
        } else {
            this.getCauses();
        }
    }
};
</script>

<style lang="scss" scoped>
.causes-table {
    height: 70vh;
}

.inline-button :deep(.q-btn__wrapper) {
    padding: 0px;
}

// align table top with cards
:deep(.q-table__top) {
    padding: 0px;
}
</style>
